import { onMounted, onUnmounted } from 'vue';
import { CANONICAL_HOST, CANONICAL_ORIGIN, DEFAULT_TITLE, PAGE_SEO } from '@/helpers/pageSeo';

export type SeoPath = keyof typeof PAGE_SEO;

const upsertDescription = (description: string): HTMLMetaElement => {
	const existing = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
	if (existing) {
		existing.content = description;
		return existing;
	}

	const meta = document.createElement('meta');
	meta.name = 'description';
	meta.content = description;
	document.head.appendChild(meta);
	return meta;
};

const upsertCanonical = (canonicalPath: string): HTMLLinkElement | null => {
	if (window.location.hostname !== CANONICAL_HOST) {
		return null;
	}

	const href = `${CANONICAL_ORIGIN}${canonicalPath}`;
	const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
	if (existing) {
		existing.href = href;
		return existing;
	}

	const link = document.createElement('link');
	link.rel = 'canonical';
	link.href = href;
	document.head.appendChild(link);
	return link;
};

// Restores the tags a Pages Function may have injected into the served HTML
// for `/`, `/login`, `/login/teacher`, or `/sign-up`, for navigations that
// resolve to a route none of those pages ever mounts on (e.g. an already
// signed-in visitor hitting `/`, which redirects straight to the dashboard
// without any usePageSeo() component — and therefore without its onUnmounted
// cleanup — ever running).
export const resetPageSeoTags = (): void => {
	document.title = DEFAULT_TITLE;
	document.head.querySelector('meta[name="description"]')?.remove();
	document.head.querySelector('link[rel="canonical"]')?.remove();
};

export const usePageSeo = (path: SeoPath): void => {
	const seo = PAGE_SEO[path];

	let descriptionMeta: HTMLMetaElement | null = null;
	let canonicalLink: HTMLLinkElement | null = null;

	onMounted(() => {
		document.title = seo.title;
		descriptionMeta = upsertDescription(seo.description);
		canonicalLink = upsertCanonical(seo.canonicalPath);
	});

	onUnmounted(() => {
		document.title = DEFAULT_TITLE;
		descriptionMeta?.remove();
		canonicalLink?.remove();
		descriptionMeta = null;
		canonicalLink = null;
	});
};
